import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useDraft } from '../../state/draft';
import { tailorPlan } from '../../state/tailor';
import { markAutoBuild } from '../../state/autoBuild';
import { stashDraft } from '../../state/draftStash';
import { useSession } from '../../state/useSession';
import { isLeavingForSignIn } from '../../api/oauth';
import { pageIsLeaving } from '../../platform/leaving';
import { ApiError } from '../../api/client';
import { funnelStep, type FunnelProps } from '../../analytics/funnel';

/**
 * V7's last question pressed (screens/new/Questions.tsx): plan the composed prompt HERE, with the
 * button saying so, and open the plan once it is ready — the idea screen's way (Idea.tsx `go`), so the
 * plan screen never opens on a scaffold that changes under them. `auto` (variant a) asks the plan
 * screen to press its own Build once it is there (state/autoBuild.ts), so a never sees it.
 */
export function usePlanRequest(): { waiting: boolean; request: (goal: string, opts: { auto: boolean }) => void } {
  const navigate = useNavigate();
  const { isAuthenticated } = useSession();
  const [waiting, setWaiting] = useState(false);
  const inFlight = useRef<number | null>(null);

  useEffect(() => {
    const onHide = (): void => {
      if (inFlight.current === null) return;
      funnelStep('plan_abandoned', { seconds: planSeconds(Date.now() - inFlight.current) }, { now: true });
    };
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, []);

  function request(text: string, opts: { auto: boolean }): void {
    const goal = text.trim();
    if (!goal || waiting) return;
    const draft = useDraft.getState();
    draft.setDescription(goal);
    // A plan from a previous idea is never built for this one.
    draft.setPlan(null);
    if (opts.auto) markAutoBuild();
    const startedAt = Date.now();
    inFlight.current = startedAt;
    funnelStep('plan_requested', { auto: opts.auto });
    setWaiting(true);
    // Put away first, signed out: the account comes later, and a phone can drop the page on the way.
    if (!isAuthenticated) void stashDraft('').catch(() => undefined);
    tailorPlan(goal).then(
      () => {
        inFlight.current = null;
        funnelStep('plan_ready', { seconds: planSeconds(Date.now() - startedAt) });
        navigate('/new/plan');
      },
      (err: unknown) => {
        inFlight.current = null;
        // Cut off by the page leaving (Apple, a reload): nothing failed.
        if (isLeavingForSignIn() || pageIsLeaving()) return;
        funnelStep('plan_failed', { reason: failureReason(err) });
        // The plan screen opens on its scaffold and says why, as it does from the idea screen.
        navigate('/new/plan');
      },
    );
  }

  return { waiting, request };
}

function planSeconds(ms: number): NonNullable<FunnelProps['seconds']> {
  const s = ms / 1000;
  return s < 5 ? '<5' : s < 10 ? '5-10' : s < 20 ? '10-20' : '20+';
}

function failureReason(err: unknown): NonNullable<FunnelProps['reason']> {
  if (err instanceof ApiError) {
    if (err.code === 'anon_plan_limit' || err.code === 'subscription_required' || err.kind === 'out_of_credits' || err.kind === 'rate_limited') return 'cap';
    if (err.kind === 'timeout') return 'timeout';
  }
  return 'error';
}
